import React, { useCallback, useEffect, useState } from 'react';
import { Mail, Reply, Trash2, AlertTriangle } from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Keys match routes/contact.py.
const TOPIC_LABELS = {
  general: 'General', support: 'Support', billing: 'Billing', sales: 'Sales',
  partnership: 'Partnership', feedback: 'Feedback', privacy: 'Privacy request',
};
const FILTERS = [
  [null, 'Inbox'],
  ['new', 'New'],
  ['read', 'Read'],
  ['archived', 'Archived'],
  ['spam', 'Spam'],
];
const STATUS_BADGE = {
  new: 'bg-violet-50 text-violet-700',
  read: 'bg-gray-100 text-gray-600',
  archived: 'bg-gray-100 text-gray-400',
  spam: 'bg-red-50 text-red-600',
};
const UNDELIVERED = { failed: 'Email not sent (delivery failed)', not_configured: 'Email not sent (email is not set up)' };

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const formatWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/** Admin Portal → Contact messages. The API allows admins and super admins only. */
export default function AdminContactMessages() {
  const [filter, setFilter] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);              // 'forbidden' | 'failed' | null
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ limit: '50', page: '1' });
    if (filter) qs.set('status', filter);
    try {
      const r = await fetch(`${API_URL}/api/admin/contact-messages?${qs}`, { headers: authHeaders() });
      if (r.status === 401 || r.status === 403) { setError('forbidden'); return; }
      if (!r.ok) { setError('failed'); return; }
      setData(await r.json());
    } catch {
      setError('failed');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, request) => {
    setBusyId(id);
    try {
      const r = await fetch(`${API_URL}/api/admin/contact-messages/${id}`, request);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        window.alert(typeof d.detail === 'string' ? d.detail : 'That didn’t work. Please try again.');
        return;
      }
      await load();
    } catch {
      window.alert('That didn’t work. Please check your connection and try again.');
    } finally {
      setBusyId(null);
    }
  };
  const setStatus = (id, status) => act(id, {
    method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
  });
  const remove = (m) => {
    if (!window.confirm(`Delete the message from ${m.name} permanently? This cannot be undone.`)) return;
    act(m.id, { method: 'DELETE', headers: authHeaders() });
  };

  const counts = data.counts || {};
  const countFor = (key) => (key ? counts[key] : (counts.new || 0) + (counts.read || 0) + (counts.archived || 0));

  return (
    <AdminLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
        <Mail className="w-5 h-5 text-violet-600" aria-hidden="true" /> Contact messages
      </h1>
      <p className="text-sm text-gray-500 mb-6">Messages sent through the website’s contact form. Each one is also emailed to the support inbox.</p>

      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Filter messages">
        {FILTERS.map(([key, label]) => (
          <button
            key={label}
            type="button"
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === key ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}
          >
            {label}{countFor(key) !== undefined ? ` (${countFor(key) ?? 0})` : ''}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="h-40 flex items-center justify-center"><div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : error ? (
          <div role="alert" className="py-12 px-6 text-center text-sm text-gray-600">
            {error === 'forbidden'
              ? <p>Only admins can read contact messages. Sign in with an admin account.</p>
              : <>
                  <p>The messages couldn’t be loaded.</p>
                  <button type="button" onClick={load} className="mt-3 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold hover:bg-gray-50">Try again</button>
                </>}
          </div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-14 text-sm text-gray-500">No messages here.</div>
        ) : (
          <ul className="divide-y divide-gray-50">
            {data.items.map((m) => {
              const undelivered = UNDELIVERED[m.delivery?.status];
              const subject = encodeURIComponent(`Re: your message to iVisora (${TOPIC_LABELS[m.topic] || 'General'})`);
              return (
                <li key={m.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                      <p className="text-xs text-gray-500 break-all">
                        <a href={`mailto:${m.email}`} className="hover:underline">{m.email}</a>
                        {m.company ? ` · ${m.company}` : ''} · {formatWhen(m.created_at)}
                      </p>
                    </div>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">{TOPIC_LABELS[m.topic] || m.topic}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[m.status] || ''}`}>{m.status}</span>
                  </div>
                  <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap break-words">{m.message}</p>
                  {undelivered && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700">
                      <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> {undelivered} — reply from here.
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <a href={`mailto:${m.email}?subject=${subject}`} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100">
                      <Reply className="w-3.5 h-3.5" aria-hidden="true" /> Reply
                    </a>
                    {m.status === 'new' && <button type="button" disabled={busyId === m.id} onClick={() => setStatus(m.id, 'read')} className="px-2.5 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Mark as read</button>}
                    {m.status === 'read' && <button type="button" disabled={busyId === m.id} onClick={() => setStatus(m.id, 'new')} className="px-2.5 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Mark as unread</button>}
                    {m.status !== 'archived' && m.status !== 'spam' && <button type="button" disabled={busyId === m.id} onClick={() => setStatus(m.id, 'archived')} className="px-2.5 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Archive</button>}
                    {m.status === 'archived' && <button type="button" disabled={busyId === m.id} onClick={() => setStatus(m.id, 'read')} className="px-2.5 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Move to inbox</button>}
                    {m.status !== 'spam'
                      ? <button type="button" disabled={busyId === m.id} onClick={() => setStatus(m.id, 'spam')} className="px-2.5 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Mark as spam</button>
                      : <button type="button" disabled={busyId === m.id} onClick={() => setStatus(m.id, 'new')} className="px-2.5 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Not spam</button>}
                    <button type="button" disabled={busyId === m.id} onClick={() => remove(m)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-red-50 text-red-600 hover:bg-red-100">
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" /> Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {!loading && !error && data.total > data.items.length && (
        <p className="mt-3 text-xs text-gray-500">Showing the newest {data.items.length} of {data.total}.</p>
      )}
    </AdminLayout>
  );
}
